<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Pracownicy</span>
        <h3 class="page-title">Lista pracowników</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end" v-if="user.type === 'ADMIN'">
        <d-button theme="success"
                  @click.prevent="$router.push('/pracownicy/nowy')">
          <i class="material-icons">add</i>
          Dodaj pracownika
        </d-button>
      </d-col>
    </d-row>

    <d-row
            v-if="loaded"
            style="width: 100%;"
            class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
              ref="workers-filters-form"
              tag="form"
              style="width: 100%;"
              class="d-flex">
        <d-input
                placeholder="Podaj imię, nazwisko lub e-mail pracownika"
                v-model="filters.search" />

        <d-input
                style="width: 360px;"
                class="ml-2"
                v-model="filters.position"
                placeholder="Stanowisko"
                :searchable="false"
                label="text">
        </d-input>

        <v-select
                style="max-width: 360px;"
                class="ml-2 ss-custom-select"
                :options="colorOptions"
                v-model="filters.color_id"
                :reduce="color => color.value"
                placeholder="Firma"
                :searchable="false"
                label="value">
                <template slot="option" slot-scope="data">
                  <div :style="{borderLeftColor: data.color}" class="color-option">{{ data.text }}</div>
                </template>
                <template slot="selected-option" slot-scope="data">
                  <div :style="{borderLeftColor: data.color}" class="color-selected">{{ data.text }}</div>
                </template>
        </v-select>

        <d-button
                theme="info"
                outline
                class="text-nowrap ml-2"
                @click.prevent="validateFiltersForm">Szukaj</d-button>

        <d-button
                theme="danger"
                outline
                class="text-nowrap ml-2"
                @click.prevent="clearFilters">Resetuj filtry</d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
        <tr>
          <th scope="col" class="border-top-0 border-bottom-0" style="border-left: 10px solid transparent;">Imię i nazwisko</th>
          <th scope="col" class="border-0">Adres e-mail</th>
          <th scope="col" class="border-0">Telefon</th>
          <th scope="col" class="border-0">Stanowisko</th>
          <th scope="col" class="border-0">Firma</th>
          <th scope="col" class="border-0">Stopka</th>
          <th scope="col" class="border-0" v-if="user.type === 'ADMIN'">Akcje</th>
        </tr>
        </thead>
        <tbody v-if="loaded">
        <tr
                v-for="(worker, index) of workers"
                :key="'worker-' + index">
          <td
                  class="align-middle"
                  style="border-left-width: 10px;border-left-style: solid;"
                  :style="{'border-left-color' : workerColor(worker)}">
            {{ worker.name_surname }}
          </td>
          <td class="align-middle">
            {{ worker.email }}
          </td>
          <td class="align-middle">
            {{ worker.phone }}
          </td>
          <td class="align-middle">
            {{ worker.position }}
          </td>
          <td class="align-middle">
            {{ worker.color.name }}
          </td>
          <td
                  class="align-middle"
                  style="width: 230px;">
            <d-button
                    theme="primary"
                    @click.native="goToUrl(worker.mac_signature)"
                    target="_blank"
                    class="mr-2"
                    title="OSX Mail">
              <i class="material-icons">mail</i> OSX Mail
            </d-button>
            <d-button
                    theme="secondary"
                    :disabled="worker.id === removeInProgress"
                    @click.native="goToUrl(worker.outlook_signature)"
                    target="_blank"
                    title="Outlook">
              <i class="material-icons">mail</i> Outlook
            </d-button>
          </td>
          <td
                  v-if="user.type === 'ADMIN'"
                  class="align-middle"
                  style="width: 150px;">
            <d-button
                    theme="primary"
                    @click.native="$router.push('/pracownicy/edycja/' + worker.id)"
                    class="mr-2"
                    title="Edytuj">
              <i class="material-icons">edit</i>
            </d-button>
            <d-button
                    theme="danger"
                    :disabled="worker.id === removeInProgress"
                    @click.native="removeWorker(worker.id)"
                    title="Usuń">
              <i class="material-icons">delete</i>
            </d-button>
          </td>
        </tr>
        <tr v-if="workers.length === 0">
          <td
                  colspan="5"
                  class="text-center">
            Brak pracowników do wyświetlenia&hellip;
          </td>
        </tr>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>

      <d-alert
              v-if="loadError"
              show
              theme="warning">
        Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
      </d-alert>
    </d-card>

    <pagination :meta-data="paginationMetaData" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import ColorUtils from './../utils/ColorUtils';
import SanitizeUtils from './../utils/SanitizeUtils';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';

export default {
  name: 'workers',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  data() {
    return {
      workers: [],
      colorOptions: [],
      user: {},
      loaded: false,
      loadError: false,
      removeInProgress: false,
      filters: {
        search: '',
        position: '',
        color_id: 0,
      }
    };
  },
  async mounted () {
    this.user = await SanitizeUtils.getUser(this);
    this.colorOptions = await ColorUtils.getColorOptions(this)
    this.loadFilteredData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    goToUrl (url) {
      window.open(url, '_blank')
    },
    workerColor (worker) {
      const color = worker.color ? worker.color.color : undefined
      if (!color) {
        return '#fff'
      }
      return color
    },
    loadFilteredData () {
      let where = {
        search: this.filters.search,
        position: this.filters.position,
        color_id: this.filters.color_id,
      };

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/workers/items?page=' + this.currentPage,
        listField: 'workers',
        params: {
          where
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: '',
        position: '',
        color_id: 0
      };

      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['workers-filters-form'], this.debouncedLoadFilteredData);
    },
    removeWorker (workerID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/workers/delete/',
        id: workerID,
        successTitle: 'Usunięto pracownika',
        successText: 'Wybrany pracownik został usunięty',
        confirmTitle: 'Czy na pewno chcesz usunąć tego pracownika?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie pracownika nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    }
  }
};
</script>
